import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Drop, ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: dramatic image panel */}
      <div className="hidden lg:flex w-1/2 bg-[#0B1726] relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/5421109/pexels-photo-5421109.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Water splash"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-14 text-white">
          <img src="/adpw-logo.png" alt="All Day Power Washing" className="w-48 h-auto drop-shadow-xl" />
          <div>
            <h1 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight">
              Run the whole<br />operation from<br /><span className="text-[#00B4D8]">one command center.</span>
            </h1>
            <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
              Clients, quotes, scheduling, crews and invoices — the field-service CRM built for All Day Power Washing.
            </p>
          </div>
          <div className="text-xs text-slate-500 uppercase tracking-widest">Professional Exterior Cleaning</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/adpw-logo.png" alt="All Day Power Washing" className="h-20 w-auto" />
          </div>
          <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Welcome back</h2>
          <p className="text-slate-500 mt-2 mb-8">Sign in to your dashboard</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@alldaypowerwashing.com" className="mt-1.5" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" required />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-[#0B1726] hover:bg-slate-800 h-11 group">
              {loading ? "Signing in..." : (<>Sign in <ArrowRight size={18} className="ml-1 group-hover:translate-x-0.5 transition-transform duration-200" /></>)}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
